<template>
  <div id="content">
    <p id="section1" class="center">
      <img src="@/assets/images/bookContent/top_bg.png" />
    </p>
    <h1 id="section2" class="chapter_title">
      <span>项目一</span>
      <span>搭建机器学习开发环境</span>
    </h1>
    <h2 id="section2-1" class="target_title">项目目标</h2>
    <div id="section2-1-1">
      <el-timeline class="content_w_710">
        <el-timeline-item timestamp="知识目标" placement="top">
          <el-card>
            <p>理解机器学习的基本概念。</p>
            <p>了解机器学习的应用领域。</p>
            <p>掌握机器学习的基本类型与一般过程。</p>
            <p>了解Python机器学习常用库。</p>
          </el-card>
        </el-timeline-item>
        <el-timeline-item timestamp="技能目标" placement="top">
          <el-card>
            <p>能够成功搭建机器学习的开发环境。</p>
            <p>能够使用Jupyter Notebook编写简单程序。</p>
          </el-card>
        </el-timeline-item>
        <el-timeline-item timestamp="素养目标" placement="top">
          <el-card>
            <p>学习机器学习的基础知识，加强对新技术的了解，增强探究意识。</p>
            <p>了解时代新科技，激发学习兴趣和创新思维，增强民族自信心。</p>
          </el-card>
        </el-timeline-item>
      </el-timeline>
    </div>
    <h2 id="section2-2" class="target_title">项目描述</h2>
    <p id="section2-2-1" class="content_box content_w_710">
      <span id="section2-2-1-1">近年来，随着互联网技术和智能硬件设备的不断发展，</span><a
        id="section2-2-1-2"
        href="https://baike.baidu.com/item/%E4%BA%BA%E5%B7%A5%E6%99%BA%E8%83%BD/9180?fr=ge_ala"
        target="_blank"
        class="bubble-font"
        >人工智能<img
          src="@/assets/images/bookContent/bubble-icon-baidu.png"
          alt=""
          class="bubble-icon" /></a
      ><span id="section2-2-1-3">已经渗透到了人们生活、工作和学习的方方面面。作为人工智能的关键技术，机器学习也就成了一个热门话题。无处不在的数据、计算能力的增强及存储技术的发展，使得机器学习越来越受重视，机器学习技术成为众多行业关注的焦点。小旌也关注到了这一点，想加入机器学习的队伍中。</span>
    </p>
    <p id="section2-2-2" class="content_box content_w_710">
      <span id="section2-2-2-1">了解到Python语言在人工智能、大数据、网络爬虫、系统运维等方面都有着广泛应用，因此，</span><a
        id="section2-2-2-2"
        href="https://www.wenjingketang.com/"
        target="_blank"
        class="bubble-font"
        >小旌<img
          src="@/assets/images/bookContent/bubble-icon-link.png"
          alt=""
          class="bubble-icon" /></a
      ><span id="section2-2-2-3">决定使用Python语言进行开发。Python语言具有数量庞大且功能相对完善的标准库和第三方库。通过对这些库的引用，能够实现不同领域业务的开发。然而，由于库的数量庞大，安装、管理这些库，以及对库进行及时升级维护成为一件复杂的事情。因此，找到“已经集成好必要库的Python开发环境”就变得尤为重要。</span>
    </p>
    <p id="section2-2-3" class="content_box content_w_710">
      小旌查阅资料发现，Anaconda集成了包含NumPy、SciPy、Pandas、Matplotlib、Scikit-learn等机器学习常用库在内的180多个工具包，使用Anaconda可一次性安装Python开发环境及大量的第三方库。于是，小旌决定使用Anaconda来完成机器学习开发环境的搭建。
    </p>
    <h2 id="section2-3" class="target_title">项目分析</h2>
    <p id="section2-3-1" class="content_box content_w_710">按照项目要求，使用Anaconda搭建机器学习开发环境的具体步骤分解如下。</p>
    <p id="section2-3-2" class="content_box content_w_710">
      第 1 步：下载Anaconda，从Anaconda的官方网站或者国内镜像站点下载Anaconda软件包。
    </p>
    <p id="section2-3-3" class="content_box content_w_710">
      第2步：双击下载好的Anaconda安装程序，根据安装步骤完成Anaconda的安装。
    </p>
    <p id="section2-3-4" class="content_box content_w_710">
      第3步：启动Jupyter Notebook，使用Jupyter Notebook编辑、运行和调试程序。
    </p>
    <p id="section2-3-5" class="content_box content_w_710">
      学习知识的时候，要知其然，还要知其所以然。为更好地进行机器学习的开发，本项目将对相关知识进行介绍，包含机器学习的概念与应用领域、机器学习的类型、机器学习的一般过程，以及Python机器学习常用库。
    </p>
    <h2 id="section2-4" class="target_title">项目准备</h2>
    <p id="section2-4-1" class="content_box content_w_710">
      全班学生以3～5人为一组进行分组，各组选出组长，组长组织组员扫码观看“人工智能、机器学习与深度学习的关系”和“机器学习的发展历史”视频，讨论并回答下列问题。
    </p>
    <div id="section2-4-2" class="two-video-box content_w_710">
      <div class="two-img-box">
        <div @click="playVideo(0)">
          <img src="@/assets/images/bookContent/single_video_1.png" alt="" />
          <p>
            <span><i class="el-icon-caret-right icon"></i></span>
          </p>
        </div>
        <span class="video-title">人工智能、机器学习与深度学习的关系</span>
      </div>
      <div class="two-img-box">
        <div @click="playVideo(1)">
          <img src="@/assets/images/bookContent/single_video_2.png" alt="" />
          <p>
            <span><i class="el-icon-caret-right icon"></i></span>
          </p>
        </div>
        <span class="video-title">机器学习的发展历史</span>
      </div>
    </div>
    <!-- <div class="special-module wttl-module content_w_710">
      <img src="@/assets/images/bookContent/titile_bg_wttl.png" />
      <div class="special-module-content">
        <p>1、画出人工智能、机器学习与深度学习的关系图。</p>
        <p>2、什么是人工智能？</p>
        <p>3、简述机器学习的发展历程。</p>
      </div>
    </div> -->
    <h3 class="section-box" id="section2-5">
      <span class="section-txt">
        1.1<br />
        机器学习的概念与应用领域
      </span>
    </h3>
    <!-- 一级标题样式 -->
    <h4 class="title_1 content_w_710" id="section2-5-1">1.1.1 机器学习的概念</h4>
    <!-- 小标题 -->
    <h5 class="title_2 content_box content_w_710" id="section2-5-1-1">1．什么是机器学习</h5>
    <p class="content_box content_w_710" id="section2-5-1-1-1">
      人们往往会有这样的经历：看到微雨过后的晚霞，就能预测出明天是一个好天气；看到色泽青绿、根蒂蜷缩、敲起来声音浊响的西瓜，就认为是一个好西瓜。
      这是因为在生活中人们已经遇到过很多类似情况，根据生活经验就可以做出有效判断。
      那么，计算机呢？是不是也能做出类似的预测呢？
      机器学习正是这样的一门学科，它致力于研究如何使计算机能够模拟人的学习行为，实现自主获取新知识，并重新组织已有的知识结构，不断提升自身解决问题的能力。
      机器学习过程与人类通过经验预测未来的过程类似，如图1-1所示。
    </p>
    <div class="demo-image__preview one-img-box center" id="section2-5-1-1-2">
      <el-image :src="srcList[0]" :preview-src-list="srcList"> </el-image>
    </div>
    <span class="little_txt" id="section2-5-1-1-3">图 1-1 机器学习与人类思考的对比</span>
    <p class="content_box content_w_710" id="section2-5-1-1-4">
      人类通过经验归纳出相应规律来解决新问题，而机器学习通过“历史数据”训练出一个“模型”，运用模型预测新的未知问题。这里的“历史数据”对应于人类的经验；“模型”对应于人类总结出的规律（即习得的结果）；“训练”对应于人类通过经验归纳出规律的过程；“算法”对应于人类归纳规律时所用的方法。
    </p>
    <p class="content_box content_w_710" id="section2-5-1-1-5">
      在学术界，机器学习还没有一个公认且准确的定义。目前，认可度比较高的定义有如下两个。
    </p>
    <p class="content_box content_w_710" id="section2-5-1-1-6">
      （1）亚瑟·塞缪尔的定义：机器学习是一个研究领域，让计算机无须进行显著式编程就具备学习能力。
    </p>
    <p class="content_box content_w_710" id="section2-5-1-1-7">
      什么是“显著式编程”？举例说明，假如要让计算机识别菊花和玫瑰花，人为地告诉计算机菊花是黄色的，玫瑰花是红色的。那么，计算机“看到”黄色的花就认为是菊花，“看到”红色的花就认为是玫瑰花，这样的编程方式就是“显著式编程”。
    </p>
    <p class="content_box content_w_710" id="section2-5-1-1-8">
      但是，如果给计算机一批菊花的图片和一批玫瑰花的图片，然后编写程序，让计算机自己总结出识别菊花和玫瑰花的规律，再来辨认菊花和玫瑰花。这种让计算机自己总结规律的编程方式是“非显著式编程”。
    </p>
    <p class="content_box content_w_710" id="section2-5-1-1-9">
      （2）汤姆·米切尔的定义：一个计算机程序被称为可以学习，是指它针对某个任务T和某个性能指标P，能够从经验E中去学习。这种学习的特点是，它在T上的被P所衡量的性能，会随着经验E的增加而提高。
    </p>
    <p class="content_box content_w_710" id="section2-5-1-1-10">
      在识别菊花和玫瑰花的例子中，任务T就是编写计算机程序识别菊花和玫瑰花；经验E就是给计算机输入一批菊花和玫瑰花的图片；而性能指标P可以认为是能正确识别菊花和玫瑰花的概率。
    </p>
    <p class="content_box content_w_710" id="section2-5-1-1-11">
      综合分析学者们的描述，机器学习可以这样理解：机器学习（machine learning,
      ML）是研究计算机怎样模拟或实现人类的学习行为，以获取新知识或技能的技术，是一门通过编程让计算机从数据中进行学习的科学。
    </p>
    <!-- 小标题 -->
    <h5 class="title_2 content_box content_w_710" id="section2-5-1-2">2．机器学习的相关术语</h5>
    <p class="content_box content_w_710" id="section2-5-1-2-1">
      机器学习过程中的相关术语包含数据相关术语、训练模型相关术语和获得模型后的相关术语。
    </p>
    <p class="content_box content_w_710" id="section2-5-1-2-2">
      （1）数据相关术语。①
      机器学习的基础是大量的数据，具有相似结构的数据样本集合称为数据集；②
      数据集的每条记录是关于一个事件或对象的描述，称为一个样本或示例；③
      反映事件或对象在某方面的表现或性质的事项，称为特征或属性；④
      属性上的取值称为特征值或属性值；⑤ 描述样本特征参数的个数称为维数。
    </p>
    <p class="content_box content_w_710" id="section2-5-1-2-3">
      以计算机识别图像中的动物是否是猫为例，其数据集、样本、特征、特征值如图1-2所示。
    </p>
    <div class="demo-image__preview one-img-box" id="section2-5-1-2-4">
      <el-image :src="srcList[1]" :preview-src-list="srcList"> </el-image>
    </div>
    <span class="little_txt" id="section2-5-1-2-5">图1-2 数据相关术语</span>
    <p class="content_box content_w_710" id="section2-5-1-2-6">
      （2）训练模型相关术语。① 从数据中学习得到模型的过程称为训练或学习；②
      训练过程中使用的数据称为训练数据，每个样本称为训练样本，训练样本组成的集合称为训练集；③
      为得到效果最佳的模型，用来调整模型参数的样本称为验证样本，验证样本组成的集合称为验证集。
    </p>
    <p class="content_box content_w_710" id="section2-5-1-2-7">
      （3）获得模型后的相关术语。①
      使用模型对未知数据进行预测的过程称为测试，用于预测的样本称为测试样本，测试样本组成的集合称为测试集；②
      模型适用于新样本的能力，称为泛化能力。
    </p>
    <!-- 一级标题样式 -->
    <h4 class="title_1 content_w_710" id="section2-5-2">1.1.2 机器学习的应用领域</h4>
    <p class="content_box content_w_710" id="section2-5-2-1">
      技术的不断进步，使得机器学习的应用领域越来越宽广，应用效果也越来越显著。总体来说，机器学习的应用主要集中在语音识别、计算机视觉、自然语言处理、自动驾驶与大数据分析等领域。
    </p>
    <div class="one-video-box content_w_710 center" id="section2-5-2-2">
      <div class="one-img-box flex">
        <img src="@/assets/images/bookContent/single_video.png" />
        <div class="v-title">
          <p>世上无难事</p>
          <p style="float: right">——机器学习入门</p>
        </div>
      </div>
      <div class="play-btn flex" @click="playVideo(2)">
        <i class="icon el-icon-video-play"></i>点击观看视频
      </div>
    </div>
    <!-- 小标题 -->
    <p class="title_2 content_box content_w_710" id="section2-5-2-3">1．语音识别</p>
    <p class="content_box content_w_710" id="section2-5-2-3-1">
      语音识别是让机器理解人说话的声音信号，并将其转换成文字的过程，它是机器学习较早的应用领域。语音识别算法是语音输入法、人机对话系统等应用的关键技术。
    </p>
    <p class="content_box content_w_710" id="section2-5-2-3-2">
      目前，运用语音识别技术的很多产品已经进入了我们的生活。例如，智能家居中，只要与智能音箱进行简单对话，就能控制家电的开关状态，给人们的生活带来了极大的便利。
    </p>
    <p class="title_2 content_box content_w_710" id="section2-5-2-4">2．计算机视觉</p>
    <p class="content_box content_w_710" id="section2-5-2-4-1">
      计算机视觉是研究如何让机器“看”的科学。目前常用的计算机视觉技术包含人脸识别、指纹识别、车牌识别等。其目的在于使用计算机代替人眼，对目标进行识别、跟踪，以及估计目标的大小与距离等。
    </p>
    <p class="content_box content_w_710" id="section2-5-2-4-2">
      机器学习是计算机视觉的重要基础，计算机视觉的各个环节都需要机器学习算法。例如，目前常用的人脸识别就用到了深度学习中的算法。
    </p>
    <div class="special-module content_box content_w_710" id="section2-5-2-4-3">
      <img src="@/assets/images/bookContent/titile_bg_tzyd.png" />
      <div class="special-module-content">
        <p>计算机视觉与机器视觉的区别。</p>
        <p>
          第一，概念不同。计算机视觉是利用计算机及其辅助设备来模拟人的视觉功能，实现对客观世界三维场景的感知、识别和理解；机器视觉就是用机器代替人眼进行测量和判断。
        </p>
        <p>
          第二，侧重点不同。计算机视觉侧重理论算法的研究，强调理论；而机器视觉侧重工程的应用，强调实时性、高精度和高速度。
        </p>
      </div>
    </div>
    
    <p class="title_2 content_box content_w_710" id="section2-5-2-5">3．自然语言处理</p>
    <p class="content_box content_w_710" id="section2-5-2-5-1">
      自然语言处理（见图 1-3）是计算机科学与语言学相结合而产生的一个应用领域，主要研究使用电子计算机模拟人的语言交际过程，使计算机能理解和运用人们生活中使用的自然语言，实现人机之间的自然语言通信，从而进一步实现计算机代替人进行部分脑力劳动的目标。其中，部分脑力劳动主要包括查询资料、解答问题、摘录文献、汇编资料，以及一切与自然语言信息有关的加工处理。
    </p>
    <div class="demo-image__preview one-img-box content_w_710" id="section2-5-2-5-2">
      <el-image :src="srcList[2]" :preview-src-list="srcList"> </el-image>
    </div>
    <span class="little_txt" id="section2-5-2-5-3">图1-3 自然语言处理</span>
    <p class="content_box content_w_710" id="section2-5-2-5-4">
      自然语言处理通常包括自然语言分析和自然语言生成两方面内容。自然语言分析包括分词方法、命名实体识别、句法分析、语义分析等方面的研究，这些方面的研究都以机器学习技术为基础，如对分词方法的研究会涉及
      <!--<span
        class="bubble-font" @click.stop="bullleClick"
        >隐马尔可夫模型
        <span
        v-if="bubbleShow1"
      class="bubble-box"
      ><span class="bubble-arrow"></span
      ><span class="bubble-big-tit" >隐马尔可夫模型</span
      ><span class="bubble-cont"
        >隐马尔可夫模型（hidden markov
        model，HMM）是一种统计模型，用于描述一个含有隐含未知参数的马尔可夫过程。它由Leonard
        E.
        Baum等人于1966年提出，并成功应用于语音识别、机器翻译、拼写错误、手写体识别、图像处理、基因序列分析等多个领域。</span
      ></span
    >
      </span>-->
      <el-popover
        placement="top"
        title="隐马尔可夫模型"
        width="350"
        trigger="click"
        content="隐马尔可夫模型（hidden markov
        model，HMM）是一种统计模型，用于描述一个含有隐含未知参数的马尔可夫过程。它由Leonard E.Baum等人于1966年提出，
        并成功应用于语音识别、机器翻译、拼写错误、手写体识别、图像处理、基因序列分析等多个领域。"
      >

        <span slot="reference" class="bubble-font"
          >隐马尔可夫模型<img
            src="@/assets/images/bookContent/bubble.png"
            alt=""
            class="bubble-icon"
        /></span>
      </el-popover>

      ；自然语言生成是将存储于计算机中的数据转化为人们能够理解的自然语言。
    </p>
    <div class="special-module content_box content_w_710" id="section2-5-2-5-5">
      <img src="@/assets/images/bookContent/titile_bg_gsdb.png" />
      <div class="special-module-content">
        <p>
          语言的理解和生成是一个极为复杂的解码和编码过程。一个能够理解自然语言的计算机系统看起来就像一个人一样，它不仅能理解上下文知识和信息，还能用信息发生器进行推理。理解和书写语言的计算机系统需要具有表示上下文知识结构的某些人工智能思想，以及根据这些知识进行推理的某些技术。
        </p>
      </div>
    </div>
    <p class="title_2 content_box content_w_710" id="section2-5-2-6">4．自动驾驶</p>
    <p class="content_box content_w_710" id="section2-5-2-6-1">
      自动驾驶的研究在20世纪80年代就开始了。近几年，自动驾驶汽车（见图1-4）获得了飞跃式的发展。奥迪、大众
      、宝马等传统汽车公司均投入巨资研发自动驾驶汽车，目前已有产品进入市场。2011年6月，美国内华达州通过法案，成为美国第一个认可自动驾驶汽车的州，此后，其他州也相继通过类似法案。自动驾驶汽车有望在不久的将来出现在普通人的生活中，而机器学习技术则起到了“司机”的作用。
    </p>
    <div class="demo-image__preview one-img-box content_w_710" id="section2-5-2-6-2">
      <el-image :src="srcList[3]" :preview-src-list="srcList"> </el-image>
    </div>
    <span class="little_txt" id="section2-5-2-6-3">图1-4 自动驾驶汽车</span>
    <p class="title_2 content_box content_w_710" id="section2-5-2-7">5．大数据分析</p>
    <p class="content_box content_w_710" id="section2-5-2-7-1">
      机器学习与大数据的结合将产生巨大的价值。目前，机器学习技术已经在电子商务、互联网金融、旅游推荐、社交网络分析等众多行业和领域中得到广泛应用。例如，在金融领域，银行可利用机器学习技术，对消费者的刷卡数据进行统计和分类，从而获得消费者的消费习惯、消费能力和消费偏好等具有商业价值的数据信息，向消费者精准推荐各种服务（如理财或信贷服务）；电信行业可以借助以机器学习为基础的大数据处理软件，对用户信息进行处理，从而得到能够查询客户信用情况的数据，使得第三方企业可以凭借这些数据信息制订市场分析报告或对目标客户群体的行为轨迹进行分析。
    </p>
    <div class="special-module content_box content_w_710" id="section2-5-2-7-2">
      <img src="@/assets/images/bookContent/titile_bg_csyy.png" />
      <div class="special-module-content">
        <p>
          请查阅相关资料，然后分组讨论机器学习现有的其他应用领域，并畅想机器学习未来的发展。
        </p>
      </div>
    </div>
    <div class="special-module content_box content_w_710" id="section2-5-2-7-3">
      <img src="@/assets/images/bookContent/titile_bg_sy.png" />
      <div class="special-module-content">
        <p>
          2022年9月6日，“2022智能经济高峰论坛”在北京举行。本次论坛的主题为“智能经济助推实体经济高质量发展”。会上，百度智能云重磅发布全新战略“云智一体，深入产业”及“云智一体3.0”架构，还展示了在多个领域的产业智能化落地成果。其中，产业级深度学习平台——飞桨，是一个便捷的深度学习框架，官方开源算法新增了100多个，整体模型数量超过了500个。目前，智能化已经应用到了水电能、制造、政务、交通及金融等产业领域，助力提升我国产业的核心竞争力。
        </p>
      </div>
    </div>
    
    <!-- 节模板 -->
    <h3 class="section-box" id="section2-6">
      <span class="section-txt">
        1.2<br />
        机器学习的类型
      </span>
    </h3>
    <!-- 一级标题样式 -->
    <h4 class="title_1 content_w_710" id="section2-6-1">1.2.1 按学习的过程分类</h4>
    <p class="content_box content_w_710" id="section2-6-1-1">
      从学习过程来看，机器学习可分为监督学习（supervised
      learning）、无监督学习（unsupervised
      learning）、半监督学习（semi-supervised
      learning）、强化学习（reinforcement learning）和深度学习（deep
      learning）。
    </p>
    <div class="one-video-box content_w_710" id="section2-6-1-2">
      <div class="one-img-box flex">
        <img src="@/assets/images/bookContent/single_video.png" />
        <div class="v-title">
          <p>机器学习的类型扩展</p>
          <p style="float: right">——机器学习入门</p>
        </div>
      </div>
      <div class="play-btn flex" @click="playVideo(3)">
        <i class="icon el-icon-video-play"></i>点击观看视频
      </div>
    </div>
    <p class="title_2 content_box content_w_710" id="section2-6-1-3">1．监督学习</p>
    <p class="content_box content_w_710" id="section2-6-1-3-1">
      监督学习是从带有类别标签（label）的训练数据中学得一个模型，并基于此模型预测新样本标签的一种学习方式，是机器学习中使用最广泛的一种类型。监督学习的训练样本包含特征属性和类别标签两部分。例如，图1-2中所示的数据集就是一个监督学习的数据集，其中头型、颜色、体型和叫声是数据集的特征属性，是否为猫属性即为类别标签。
    </p>
    <p class="content_box content_w_710" id="section2-6-1-3-2">
      监督学习在手写文字识别、声音处理、图像处理、垃圾邮件分类与拦截等各个方面，都有广泛应用。
    </p>
    <div class="special-module content_w_710" id="section2-6-1-3-3">
      <img src="@/assets/images/bookContent/titile_bg_csyy.png" />
      <div class="special-module-content">
        <p>请查阅相关资料，然后分组讨论，列举几个监督学习的例子。</p>
      </div>
    </div>
    <p class="title_2 content_box content_w_710" id="section2-6-1-4">2．无监督学习</p>
    <p class="content_box content_w_710" id="section2-6-1-4-1">
      无监督学习是机器学习的另一大类学习方法，是在无标签的训练样本中发现数据规律的一种学习方式。无监督学习的训练样本中没有标签，这是它与监督学习最主要的区别。无监督学习在人造卫星故障诊断、视频分析、社交网站解析、数据可视化等方面有着广泛应用。另外，无监督学习还可作为监督学习方法的前处理工具。
    </p>
    <p class="title_2 content_box content_w_710" id="section2-6-1-5">3．半监督学习</p>
    <p class="content_box content_w_710" id="section2-6-1-5-1">
      半监督学习是监督学习与无监督学习结合在一起的一种学习方法。半监督学习的数据由大量的无标签数据和少量的有标签数据混合而成。因此，可通过在模型训练中引入无标签样本，来弥补监督学习训练样本不足的缺陷。
    </p>
    <p class="content_box content_w_710" id="section2-6-1-5-2">
      例如，在识别芒果是否成熟的任务中，有这样的情形：在一个芒果园中，果农拿来5个芒果说这都是成熟的芒果，然后再指着树上的3个芒果说这些还不熟。如果我们根据这8个样本训练一个机器学习模型来预测新的芒果是否成熟，这样的训练样本显然太少了。此时，就可以使用半监督学习的方法，将树上的其他芒果都用上，这样训练样本就增加了，训练出来的模型性能更高。
    </p>
    <div class="special-module content_box content_w_710" id="section2-6-1-5-3">
      <img src="@/assets/images/bookContent/titile_bg_gsdb.png" />
      <div class="special-module-content">
        <p>
          半监督学习方法训练模型时，可以先利用有标签的样本训练出一个模型，用这个模型去预测新的数据，然后询问专家，再将这个样本变为有标签样本；把这个新获得的有标签的样本加入训练集后重新训练一个模型，再去预测，依次重复，若每次都能预测出对改善模型性能帮助较大的数据，则只需询问专家较少的次数就能构建出较强的模型，从而大幅度降低标记成本。
        </p>
      </div>
    </div>
    <p class="title_2 content_box content_w_710" id="section2-6-1-6">4．强化学习</p>
    <p class="content_box content_w_710" id="section2-6-1-6-1">
      强化学习是机器学习中一个较新的领域，它能根据环境的改变而改变，从而获取最大的收益。强化学习的思想来源于心理学中的行为主义理论，即动物如何在环境给予的刺激下，逐渐形成对刺激的反应，从而产生能获得最大收益的习惯性行为。例如，婴幼儿往往是为了获得父母的表扬去做事情。强化学习正是模仿人类的这种学习行为而产生的一种学习方式。强化学习在机器人的自动控制、计算机游戏中的人工智能、市场战略的最优化等方面均有广泛应用。
    </p>
    <p class="title_2 content_box content_w_710" id="section2-6-1-7">5．深度学习</p>
    <p class="content_box content_w_710" id="section2-6-1-7-1">
      深度学习的概念来源于对人工神经网络的研究，其模型结构是一种含多隐层的神经网络。深度学习使机器能模仿人类的视听与思考等活动，解决了很多复杂的模式识别难题，使得人工智能相关技术取得了很大进步。在海量数据中，深度学习的表现要比普通的机器学习更为出色。深度学习在搜索技术、数据挖掘、机器翻译、自然语言处理、多媒体学习和个性化技术等领域都有着广泛的应用。
    </p>
    <!-- 一级标题样式 -->
    <h4 class="title_1 content_w_710" id="section2-6-2">1.2.2 按完成的任务分类</h4>
    <p class="content_box content_w_710" id="section2-6-2-1">
      从机器学习完成的任务来看，机器学习可分为回归、分类和聚类等学习任务。
    </p>
    <p class="title_2 content_box content_w_710" id="section2-6-2-2">
      <!--        <span  class="bubble-font" @click.stop="bullle2Click" style="color:#0281EC;">1．回归-->
      <!--<span
        v-if="bubbleShow2"
      class="bubble-box bubble-box_2"
      ><span class="bubble-arrow2"></span
      ><span class="bubble-big-tit" >回归</span
      ><span class="bubble-cont"
        >回归是预测一个连续值的输出（因变量）基于一个或多个输入（自变量或特征）的机器学习任务。</span
      ></span
    >-->
      <!--        </span>-->
      <el-popover
        placement="top"
        title="回归"
        width="350"
        trigger="click"
        content="回归是预测一个连续值的输出（因变量）基于一个或多个输入（自变量或特征）的机器学习任务。"
      >
        <span slot="reference" class="bubble-font"
          >1．回归<img
            src="@/assets/images/bookContent/bubble.png"
            alt=""
            class="bubble-icon"
        /></span>
      </el-popover>
    </p>
    <p class="content_box content_w_710" id="section2-6-2-2-1">
      回归属于监督学习，最初是统计学中的一种方法，用来预测某个变量的变化趋势，其预测结果是连续的值，如预测房价、预测股价等。若一个产品的实际价格为300元，通过回归分析预测值为299元，则认为这是一个比较好的回归分析。回归是对真实值的一种逼近预测。
    </p>
    <p class="content_box content_w_710" id="section2-6-2-2-2">
      在机器学习领域中，回归任务的实现需要先对数据样本点进行拟合，再根据拟合出来的函数对输入的新数据进行输出预测，如图1-5所示。
    </p>
    <div class="demo-image__preview one-img-box" id="section2-6-2-2-3">
      <el-image
        :src="srcList[4]"
        :preview-src-list="srcList"
        style="width: 50%"
      >
      </el-image>
    </div>
    <span class="little_txt" id="section2-6-2-2-4">图1-5 回归任务</span>
    <p class="content_box content_w_710" id="section2-6-2-2-5">
      图1-5中，圆点表示带有标签的训练数据；回归曲线表示经过训练后获得的回归函数或回归模型。若该回归任务表示对商品价格走势的预测（x表示年份，y表示商品价格），则由回归函数可以预测未来某年的商品价格。
    </p>
    <p class="title_2 content_box content_w_710" id="section2-6-2-3">2．分类</p>
    <p class="content_box content_w_710" id="section2-6-2-3-1">
      分类是通过在已有数据的基础上进行学习，得到一个分类模型，该模型可以将待分类的数据集映射到某个给定的类别中，从而实现数据分类。其中，分类模型也称为分类器。
    </p>
    <p class="content_box content_w_710" id="section2-6-2-3-2">
      分类属于监督学习，其数据集包含特征属性和类别标签两部分，其中类别标签是预设的类别号。例如，用机器学习模型判断一幅图上的动物是猫还是狗，假设用1代表猫，用2代表狗，那么模型的预测结果就是1或2，分别代表猫和狗。分类的最终正确结果只有一个，错误的就是错误的，不会有相近的概念。
    </p>
    <p class="content_box content_w_710" id="section2-6-2-3-3">
      根据预设的类别数目，分类模型可分为二分类和多分类。例如，判断图片上的动物是猫还是狗，是二分类问题；如果要判断图片上的动物是自然界动物中的哪一类，则属于多分类问题。
    </p>
    <p class="content_box content_w_710" id="section2-6-2-3-4">
      在机器学习领域，分类任务的实现需要先利用已有数据训练一个分类模型（类似于数据样本中的分界线），然后对输入的新数据进行预测，即根据分界线对新数据进行分类，如图1-6所示。
    </p>
    <div class="demo-image__preview one-img-box" id="section2-6-2-3-5">
      <el-image
        :src="srcList[5]"
        :preview-src-list="srcList"
        style="width: 50%"
      >
      </el-image>
    </div>
    <span class="little_txt" id="section2-6-2-3-6">图1-6 分类任务</span>
    <p class="content_box content_w_710" id="section2-6-2-3-7">
      图1-6中，实心的正方形和圆表示带有标签的训练数据；分界线表示经过训练后获得的分类模型；空心的正方形和圆表示对输入的新数据进行预测。
    </p>
    <p class="title_2 content_box content_w_710" id="section2-6-2-4">3．聚类</p>
    <p class="content_box content_w_710" id="section2-6-2-4-1">
      聚类属于无监督学习，是按照某个特定标准把一个数据集分割成不同的类，使得同一类中的数据对象之间相似性尽可能大，不同类中的数据对象之间差异性也尽可能大。可见，聚类后的数据中，同类数据尽可能聚集到一起，不同类数据尽可能分离。聚类任务中，每个类称为一个簇。
    </p>
    <p class="content_box content_w_710" id="section2-6-2-4-2">
      聚类任务常用于对目标群体进行多指标划分，如图1-7所示。例如，现有多个客户的购物记录数据，且未对数据进行标记，通过聚类任务可将具有相同购物习惯的客户汇聚成类，不同类中的客户购买的商品种类不同，店铺运营即可根据该反馈信息向客户推荐相关商品。
    </p>
    <div class="demo-image__preview one-img-box" id="section2-6-2-4-3">
      <el-image :src="srcList[6]" :preview-src-list="srcList"> </el-image>
    </div>
    <span class="little_txt" id="section2-6-2-4-4">图1-7 聚类任务</span>
    <div class="special-module content_box content_w_710" id="section2-6-2-4-5">
      <img src="@/assets/images/bookContent/titile_bg_zdmj.png" />
      <div class="special-module-content">
        <p>
          由于聚类任务中的数据没有标签，所以不知道输入数据的输出结果是什么，但是可以清晰地知道输入数据属于数据的哪一类。
        </p>
      </div>
    </div>
    <!-- 节模板 -->
    <h3 class="section-box" id="section2-7">
      <span class="section-txt">
        1.3<br />
        机器学习的一般过程
      </span>
    </h3>
    <p class="content_box content_w_710" id="section2-7-1">
      机器学习的基本思想是通过从样本数据中提取所需特征构造一个有效的模型，并使用所建模型来完成具体的任务。使用机器学习方法解决具体问题的一般过程如图1-8所示。
    </p>
    <div class="demo-image__preview one-img-box content_w_710" id="section2-7-2">
      <el-image :src="srcList[7]" :preview-src-list="srcList"> </el-image>
    </div>
    <span class="little_txt" id="section2-7-3">图1-8 机器学习的一般过程</span>
    <p class="content_box content_w_710" id="section2-7-4">
      利用机器学习解决问题时，首先要获取所研究问题的数据，其次是对获取到的数据进行适当处理，然后选取合适的算法训练模型，最后对训练好的模型进行评估，以判定其是否满足任务需求，如满足，即可使用模型。机器学习的过程是一个繁琐复杂的过程，下面介绍各个阶段需要做的一些工作。
    </p>
    <!-- 一级标题样式 -->
    <h4 class="title_1 content_w_710" id="section2-7-5">1.3.1 数据获取</h4>
    <p class="content_box content_w_710" id="section2-7-5-1">
      机器学习的第一步是收集与学习任务相关的数据，这是最基础也是最重要的一步。虽然现在是大数据时代，但对于一个给定任务，要得到与之相关的数据有时却很困难。业界广泛流传这样一句话：数据和特征决定了机器学习的上限，而模型和算法只是逼近这个上限的方法而已。因此，数据的获取尤为重要。
    </p>
    <p class="content_box content_w_710" id="section2-7-5-2">
      在训练最优的机器学习模型时，一定要选择最有代表性的数据集。只有选择最合适的属性作为特征，才能保证机器学习项目能应用于实际。
    </p>
    <!-- 一级标题样式 -->
    <h4 class="title_1 content_w_710" id="section2-7-6">1.3.2 特征提取</h4>
    <p class="content_box content_w_710" id="section2-7-6-1">
      特征提取是使用专业的背景知识和技巧最大限度地从原始数据中提取并处理数据，使得特征在机器学习的模型上得到更好的发挥，它直接影响机器学习的效果。例如，在机器自动分辨筷子和牙签两种物品的实验中，收集到的样本数据集如表1-1所示。
    </p>
    <span class="little_txt" id="section2-7-6-2">表1-1 “筷子和牙签”样本数据集</span>
    <tables
      id="section2-7-6-3"
      class="content_w_710"
      :configTable="table_01.assessConfig"
      :demoTableData="table_01.assessTableData"
    ></tables>
    <p class="content_box content_w_710" id="section2-7-6-4">
      观察表1-1中的数据集可发现，根据长度和质量这两个特征即可分辨筷子和牙签，材质这个特征对区分筷子和牙签的作用并不明显，故可在特征属性中提取长度和质量这两个特征，而将材质这个特征删除，这个过程称为特征提取。
    </p>
    <h4 class="title_1 content_w_710" id="section2-7-7">1.3.3 数据预处理</h4>
    <p class="content_box content_w_710" id="section2-7-7-1">
      现实生活中，收集到的数据往往会有数据量纲（数据的度量单位）或数据类型不一致等问题。因此，在获取样本之后，通常需要对数据进行预处理。数据预处理没有标准流程，通常包含去除唯一属性，处理缺失值、重复值和异常值，以及数据定量化等几个步骤。
    </p>
    <p class="content_box content_w_710" id="section2-7-7-2">
      表1-2的数据集是某平台上的“客户信息样本数据集”，要求使用机器学习方法，进行聚类，将客户划分为几种类型，以便为其推销相关的产品。在训练模型之前，我们需要对数据集中的数据进行预处理，才能得到理想的机器学习样本数据集。
    </p>
    <span class="little_txt" id="section2-7-7-3">表1-2 客户信息样本数据集</span>
    <tables
      id="section2-7-7-4"
      class="content_w_710"
      :configTable="table_02.assessConfig"
      :demoTableData="table_02.assessTableData"
    ></tables>
    <span class="little_txt" id="section2-7-7-5">表1-2（续）</span>
    <tables
      id="section2-7-7-6"
      class="content_w_710"
      :configTable="table_03.assessConfig"
      :demoTableData="table_03.assessTableData"
    ></tables>
    <p class="title_2 content_box content_w_710" id="section2-7-7-7">1．去除唯一属性</p>
    <p class="content_box content_w_710" id="section2-7-7-7-1">
      唯一属性通常指ID、姓名等属性，每个样本的取值都不一样且唯一，这些属性不能刻画样本自身的分布规律，在做数据预处理时，需将这些属性删除。表1-2中的数据经过“去除唯一属性”处理后的结果如表1-3所示。
    </p>
    <span class="little_txt" id="section2-7-7-7-2">表1-3 去除唯一属性后的客户信息样本数据集</span>
    <tables
      id="section2-7-7-7-3"
      class="content_w_710"
      :configTable="table_04.assessConfig"
      :demoTableData="table_04.assessTableData"
    ></tables>
    <p class="title_2 content_box content_w_710" id="section2-7-7-8">2．缺失值、重复值和异常值处理</p>
    <p class="content_box content_w_710" id="section2-7-7-8-1">
      收集到的数据集在很多时候会有缺失值，如表1-2的数据集中姓名为“李武”的客户，未能收集到他的消费数据，导致存在缺失值。常见的缺失值处理方法有3种：①
      直接使用含有缺失值的特征；② 删除含有缺失值的特征；③
      缺失值补全。其中，缺失值补全是最常用的手段。
    </p>
    <p class="content_box content_w_710" id="section2-7-7-8-2">
      缺失值补全常用的方法有均值插补法与同类均值插补法。均值插补法是指使用该属性有效值的平均值来插补缺失的值；同类均值插补法是指首先将样本进行分类或聚类，然后以该类中样本的均值插补缺失值。
    </p>
    <p class="content_box content_w_710" id="section2-7-7-8-3">
      对表1-2中的数据进行聚类，假设序号为3、4、5的客户聚为一类，采用同类均值插补法，则“李武”的年消费额属性值应补全为“15
      000”。
    </p>
    <p class="content_box content_w_710" id="section2-7-7-8-4">
      收集到的数据中，有时会有重复的记录，它会导致数据的方差变小，数据的分布发生较大变化。因此，若检查到数据集中有重复数据，要将其删除。表1-2中，第2条数据与第6条数据重复，可将其中一条数据删除。
    </p>
    <p class="content_box content_w_710" id="section2-7-7-8-5">
      异常值是指超出或低于正常范围的值，如年龄为负数、身高大于3
      m等，它会导致分析结果产生偏差甚至错误。检查到异常值后，可对异常值进行删除或替换处理。表1-3中的数据经过缺失值、重复值和异常值处理后的结果如表1-4所示。
    </p>
    <span class="little_txt" id="section2-7-7-8-6"
      >表1-4 缺失值、重复值和异常值处理后的客户信息样本数据集</span
    >
    <tables
      id="section2-7-7-8-7"
      class="content_w_710"
      :configTable="table_05.assessConfig"
      :demoTableData="table_05.assessTableData"
    ></tables>
    <p class="title_2 content_box content_w_710" id="section2-7-7-9">3．数据定量化</p>
    <p class="content_box content_w_710" id="section2-7-7-9-1">
      计算机只能处理数值型数据。因此，在数据预处理时，如果有非数值型数据，都要先转换成数值型数据。表1-2中，性别和学历都是非数值型数据，需将其处理成数值型数据，如学历“高中”可用20代替，“大专”可用40代替，“本科”可用60代替；性别中的“男”可用1代替，“女”可用2代替。表1-4中的数据经过定量化处理后的结果如表1-5所示。
    </p>
    <span class="little_txt" id="section2-7-7-9-2">表1-5 数据定量化处理后的客户信息样本数据集</span>
    <tables
      id="section2-7-7-9-3"
      class="content_w_710"
      :configTable="table_06.assessConfig"
      :demoTableData="table_06.assessTableData"
    ></tables>
    <h4 class="title_1 content_w_710" id="section2-7-8">1.3.4 数据标准化</h4>
    <p class="content_box content_w_710" id="section2-7-8-1">
      表1-5中的数据都已经做了定量化处理，但是经观察发现，有些数据的数量级差别较大（如年龄与年收入），如果直接用这样的数据训练模型，数量级较大的属性会占主导地位，导致模型较弱。因此，需对其进行标准化处理。
    </p>
    <p class="content_box content_w_710" id="section2-7-8-2">
      数据标准化是指将数据按比例缩放，使之落入一个特定区间，从而消除数据之间数量级的差异。经过标准化处理后，不同的特征可以具有相同的尺度。常用的数据标准化方法有min-max标准化和z-score标准化。
    </p>
    <p class="content_box content_w_710" id="section2-7-8-3">
      （1）min-max标准化（归一化）。数据集的每个属性（数据表中的列）中都有一个最大值和一个最小值，分别用max和min表示，然后通过一个公式将原始值映射到区间[0,1]上。其公式为
    </p>
    <p class="content_box content_w_710" id="section2-7-8-3">新值=(原始值-最小值)/(最大值-最小值)</p>
    <p class="content_box content_w_710" id="section2-7-8-4">
      表1-5中，序号为1的记录经过min-max标准化处理后的性别值为(1-1)/(2-1)=0。整个数据集的数据经过min-max标准化处理后的结果如表1-6所示。
    </p>
    <span class="little_txt" id="section2-7-8-5"
      >表1-6 min-max标准化处理后的客户信息样本数据集</span
    >
    <tables
      id="section2-7-8-6"
      class="content_w_710"
      :configTable="table_07.assessConfig"
      :demoTableData="table_07.assessTableData"
    ></tables>
    <p class="content_box content_w_710" id="section2-7-8-7">
      这种处理方法的缺点是当有新数据加入时，可能会导致最大值和最小值发生变化，需要重新定义。
    </p>
    <div class="one-video-box content_w_710" id="section2-7-8-8">
      <div class="one-img-box flex">
        <img src="@/assets/images/bookContent/single_video.png" />
        <div class="v-title">
          <p>标准差的计算方法</p>
          <p style="float: right">——机器学习入门</p>
        </div>
      </div>
      <div class="play-btn flex" @click="playVideo(4)">
        <i class="icon el-icon-video-play"></i>点击观看视频
      </div>
    </div>
    <p class="content_box content_w_710" id="section2-7-8-9">
      （2）z-score标准化（规范化）。它是基于原始数据的均值和标准差进行数据标准化的一种方法。z-score标准化方法适用于属性的最大值和最小值未知的情况或有超出取值范围的离群数据的情况。其公式为
    </p>
    <p class="content_box content_w_710" id="section2-7-8-10">新值=(原始值-均值)/标准差</p>
    <p class="content_box content_w_710" id="section2-7-8-11">
      表1-5中，序号为1的记录经过z-score标准化处理后的性别值为(1-1.4)/0.49≈-0.82。整个数据集的数据经过z-score标准化处理后的数据如表1-7所示。
    </p>
    <span class="little_txt" id="section2-7-8-12"
      >表1-7 z-score标准化处理后的客户信息样本数据集</span
    >
    <tables
      id="section2-7-8-13"
      class="content_w_710"
      :configTable="table_08.assessConfig"
      :demoTableData="table_08.assessTableData"
    ></tables>
    <div class="special-module content_box content_w_710" id="section2-7-8-14">
      <img src="@/assets/images/bookContent/titile_bg_zdmj.png" />
      <div class="special-module-content">
        <p>
          z-score标准化要求样本属性值数据服从正态分布，这就要求样本数量足够多，故此案例不适合使用z-score标准化进行数据处理。
        </p>
      </div>
    </div>
    <h4 class="title_1 content_w_710" id="section2-7-9">1.3.5 数据降维</h4>
    <p class="content_box content_w_710" id="section2-7-9-1">
      在机器学习中，“维度”是指样本集中特征属性的个数。如表1-7中有5个特征属性，则数据的维度是5。“降维”是指减少特征矩阵中特征的数量。为什么要进行降维处理呢？降维的目的有两个，一个是为了对数据进行可视化，以便对数据进行观察和探索；另外一个是简化机器学习模型的训练，使模型的泛化能力更好，避免“维度灾难”。
    </p>
    <p class="content_box content_w_710" id="section2-7-9-2">
      在实际应用中，数据一般是高维的。例如，手写的数字图片，如果将其缩放到28x28像素的大小，那么它的维度就是28x28=784维，图1-9是手写的“1”及其对应的图像二维矩阵（数据已经被规范化到[0,1]范围内）。
    </p>
    <div class="demo-image__preview one-img-box" id="section2-7-9-3" style="margin-bottom: 0">
      <el-image
        :src="srcList[8]"
        :preview-src-list="srcList"
        style="width: 70%"
      >
      </el-image>
    </div>
    <span class="little_txt" id="section2-7-9-4">图1-9 手写数字“1”及其对应二维矩阵</span>
    <p class="content_box content_w_710" id="section2-7-9-5">
      我们很难对高维数据具有直观的认识，如果把数据的维度降低到2维或者3维，并且保持数据点的关系与原高维空间里的关系不变或者近似，就可以进行可视化，用肉眼来观察数据。
    </p>
    <p class="content_box content_w_710" id="section2-7-9-6">
      那么，是不是数据维数越少越好呢？并非如此。例如，对苹果和梨子进行分类时，若只将形状作为特征，则很可能会出现错误分类的情况；若再将大小作为特征，则可减少错误分类的情况；若再将颜色作为特征，则可进一步减少错误分类的情况。可见，特征增加后，分类效果可能会更好。故得到结论：维数太多或太少都不好，设置恰当的维数对机器学习模型非常重要。
    </p>
    <p class="content_box content_w_710" id="section2-7-9-7">
      数据降维最常用的方法是主成分分析法。深度学习就是对样本的特征进行复杂的变换，得到最有效的特征，从而提高机器学习的性能。
    </p>
    <h4 class="title_1 content_w_710" id="section2-7-10">1.3.6 训练模型</h4>
    <p class="title_2 content_box content_w_710" id="section2-7-10-1">1．训练集、验证集和测试集</p>
    <p class="content_box content_w_710" id="section2-7-10-1-1">
      数据处理好之后，就可以使用精心准备的数据来训练模型了。在训练模型的过程中，需要将输入的样本数据划分为训练集、验证集和测试集3个部分：①
      训练集是用来训练模型的数据集；②
      验证集是为了调整模型的参数而设置的数据集，在模型训练过程中对模型进行评估，评估之后模型还会继续修改；③
      测试集是模型训练好之后，用来对模型进行评估的数据集。
    </p>
    <p class="content_box content_w_710" id="section2-7-10-1-2">
      训练机器学习模型时，样本数据集的选取应满足以下几点要求：①
      训练集的数据要尽可能充分且分布平衡（即每个类别的样本数量差不多），否则不可能训练出一个完好的模型；②
      验证集或测试集的样本也需要符合一定的平衡分布，否则将无法测试出一个准确的模型；③
      训练模型和测试模型使用的样本不能相同。
    </p>
    <p class="title_2 content_box content_w_710" id="section2-7-10-2">2．机器学习算法</p>
    <p class="content_box content_w_710" id="section2-7-10-2-1">
      训练机器学习模型时，要根据具体的学习任务，选择合适的算法。分类任务经常使用的算法有k近邻、朴素贝叶斯、决策树、支持向量机等；回归任务经常使用的算法有线性回归、k近邻、决策树等；聚类任务经常使用的算法有k均值、DBSCAN、GMM等。
    </p>
    <h4 class="title_1 content_w_710" id="section2-7-11">1.3.7 评估模型的有效性</h4>
    <p class="content_box content_w_710" id="section2-7-11-1">
      一个机器学习模型训练出来后，一般需要评估该模型的效果，看其是否能满足实际问题的需要。评估模型的有效性就是利用测试集对模型进行测试，评估其输出结果。
    </p>
    <p class="title_2 content_box content_w_710" id="section2-7-11-2">1．过拟合与欠拟合</p>
    <p class="content_box content_w_710" id="section2-7-11-2-1">
      什么样的模型是一个好的模型呢？事实上，我们希望得到一个在新的未知样本上表现很好的模型，即
      <!--<span class="bubble-font" @click.stop="bullle3Click">泛化能力<span
        v-if="bubbleShow3"
      class="bubble-box bubble-box_3"
      ><span class="bubble-arrow3"></span
      ><span class="bubble-big-tit" >泛化能力</span
      ><span class="bubble-cont"
        >泛化能力（generalization ability）是指机器学习算法对新鲜样本的适应能力。</span
      ></span
    ></span>-->
      <el-popover
        placement="top"
        title="泛化能力"
        width="350"
        popper-class="my-pop-over"
        style="user-select: text"
        trigger="click"
        content="泛化能力（generalization ability）是指机器学习算法对新鲜样本的适应能力。"
      >
        <span slot="reference" class="bubble-font"
          >泛化能力<img
            src="@/assets/images/bookContent/bubble.png"
            alt=""
            class="bubble-icon"
        /></span>
      </el-popover>
      好的模型。为了达到这个目的，应该从训练样本中尽量学习出适用于新样本的“普遍规律”，才能在遇到新样本时，做出正确的判别。然而，如果模型在训练样本上学得“太好”了，很可能把训练样本自身的一些特点当成了所有样本的一般性质，导致泛化能力下降，这种现象在机器学习中称为“过拟合”。与“过拟合”相对的就是“欠拟合”，指对训练样本的一般性质尚未学好。图1-10给出了关于过拟合与欠拟合的一个类比，便于理解。
    </p>
    <div class="demo-image__preview one-img-box content_w_710" id="section2-7-11-2-2" style="margin-bottom: 0">
      <el-image :src="srcList[9]" :preview-src-list="srcList"> </el-image>
    </div>
    <span class="little_txt" id="section2-7-11-2-3">图1-10 过拟合与欠拟合的直观类比</span>
    <p class="title_2 content_box content_w_710" id="section2-7-11-3">2．性能度量</p>
    <p class="content_box content_w_710" id="section2-7-11-3-1">
      对机器学习的泛化能力进行评估时，需要有衡量模型泛化能力的评价标准。不同类型的学习任务，其评价标准也是不同的。
    </p>
    <p class="content_box content_w_710" id="section2-7-11-3-2">
      （1）回归任务。回归任务中最常用的评估方法有残差、和方差、均方误差、均方根误差和确定系数（R2）等。
    </p>
    <p class="content_box content_w_710" id="section2-7-11-3-3">
      ①
      残差：在数理统计中是指所有拟合数据（即模型预测数据）与原始数据（样本实际值）之间的差的和。
    </p>
    <p class="content_box content_w_710" id="section2-7-11-3-4">
      ②
      和方差（SSE）：拟合数据和原始数据对应点的误差的平方和。SSE越接近于0，说明模型越好，数据预测也越成功。
    </p>
    <p class="content_box content_w_710" id="section2-7-11-3-5">
      ③ 均方误差（MSE）：拟合数据和原始数据对应点误差的平方和的均值，即SSE/n。
    </p>
    <p class="content_box content_w_710" id="section2-7-11-3-6">④ 均方根误差（RMSE）：MSE的平方根，也称回归任务的拟合标准差。</p>
    <p class="content_box content_w_710" id="section2-7-11-3-7">
      ⑤
      确定系数（R2）：通过数据的变化来表征一个拟合的好坏，R2的正常取值范围为[0,1]，越接近1，表明模型越好。
    </p>
    <p class="content_box content_w_710" id="section2-7-11-3-8">
      （2）分类任务。分类任务中最常用的评估方法有准确率、精确率、召回率和F1值等。下面以一个二分类问题为例，介绍这些评估方法的含义。
    </p>
    <p class="content_box content_w_710" id="section2-7-11-3-9">
      在二分类中，假设样本有正反两个类别，则分类模型预测的结果有两种，正例和反例；真实数据的标签也有两种，正例和反例。那么，预测结果与真实标签的组合就有真正例（true
      positive）、真反例（true negative）、假正例（false
      positive）和假反例（false
      negative）4种情况，分别用TP、TN、FP和FN表示以上4种情况，如表1-8所示。
    </p>
    
    <span class="little_txt" id="section2-7-11-3-10">表1-8 预测结果与真实结果的组合</span>
    <el-table :data="tableData" border id="section2-7-11-3-11" class="content_w_710">
      <el-table-column prop="date" label="真 实 值" align="center">
      </el-table-column>
      <el-table-column label="预 测 值" align="center">
        <el-table-column prop="just" label="正例" align="center">
        </el-table-column>
        <el-table-column prop="back" label="反例" align="center">
        </el-table-column>
      </el-table-column>
    </el-table>
    <p class="content_box content_w_710" id="section2-7-11-3-12">
      在表1-8中，TP表示真实值与预测值都是正样本的数量；FN表示真实值是正样本，而预测值却是反样本的数量；FP表示真实值是反样本，而预测值却是正样本的数量；TN表示真实值与预测值都是反样本的数量。可见，TP与TN都是预测正确的情况。因此，预测的准确率可定义为
    </p>
    <img class="gs_img gs_img_1" id="section2-7-11-3-13" src="@/assets/images/bookContent/gs1.png" alt="" />
    <p class="content_box content_w_710" id="section2-7-11-3-14">
      而预测的精确率表示预测为正的样本中有多少是真正的正例，故精确率可定义为
    </p>
    <img
      id="section2-7-11-3-15"
      class="gs_img gs_img_2"
      src="@/assets/images/bookContent/gs2.png"
      alt=""
      style="width: 30%"
    />
    <p class="content_box content_w_710" id="section2-7-11-3-16">召回率表示样本中的正例有多少被预测正确了，故召回率可定义为</p>
    <img
      id="section2-7-11-3-17"
      class="gs_img gs_img_3"
      src="@/assets/images/bookContent/gs3.png"
      alt=""
      style="width: 30%"
    />
    <p class="content_box content_w_710" id="section2-7-11-3-18">
      对于机器学习模型来说，当然希望精确率和召回率都保持较高的水准。但事实上，这两者在很多时候是不能兼得的。为此，提出了F1值，它同时兼顾了分类模型的精确率和召回率。它的最大值是1，最小值是0。其定义为
    </p>
    <img
      id="section2-7-11-3-19"
      class="gs_img gs_img_4"
      src="@/assets/images/bookContent/gs4.png"
      alt=""
      style="width: 45%"
    />
    <p class="content_box content_w_710" id="section2-7-11-3-20">
      （3）聚类任务。在聚类任务中，我们希望同一类的样本尽量类似，不同类的样本尽量不同。即簇内对象的相似度越大，不同簇之间的对象差别越大，聚类效果越好。聚类任务常用的评估指标如表1-9所示。
    </p>
    <span class="little_txt" id="section2-7-11-3-21">表1-9 聚类任务常用的评估指标</span>
    <tables
      id="section2-7-11-3-22"
      class="content_w_710"
      :configTable="table_09.assessConfig"
      :demoTableData="table_09.assessTableData"
    ></tables>
    <p class="content_box content_w_710" id="section2-7-11-3-23">
      表1-9中列出了6种聚类模型的评估方法，其中前4种方法需要真实值（已知类别的标签）的配合，才能评价聚类模型的优劣，后两种方法则不需要真实值的配合。一般来说，前4种方法的评价效果更好。
    </p>
    <p class="content_box content_w_710" id="section2-7-11-3-24">
      除轮廓系数评价法之外，其他5种方法都是分值越高，聚类效果越好；轮廓系数评价法需要判断不同类别数目情况下轮廓系数的走势，才能寻找最佳的聚类数目。
    </p>
    <h4 class="title_1 content_w_710" id="section2-7-12">1.3.8 使用模型</h4>
    <p class="content_box content_w_710" id="section2-7-12-1">
      经过评估，如果模型的性能能达到实际需求，就可以使用该模型预测新样本了。例如，假设区分筷子和牙签的模型训练出来并且能达到实际需求，那么，就可以将一个新样本的数据（长度为14
      cm，质量为6
      g）输入到该模型中，使用模型预测出输入的数据是筷子还是牙签，如图1-11所示。
    </p>
    <div class="demo-image__preview one-img-box" id="section2-7-12-2" style="margin-bottom: 0">
      <el-image
        :src="srcList[10]"
        :preview-src-list="srcList"
        style="width: 60%"
      >
      </el-image>
    </div>
    <span class="little_txt" id="section2-7-12-3">图1-11 使用模型预测新样本</span>
    
    <!-- 节模板 -->
    <h3 class="section-box" id="section2-8">
      <span class="section-txt">
        1.4<br />
        Python机器学习常用库
      </span>
    </h3>
    <h4 class="title_1 content_w_710" id="section2-8-1">1.4.1 NumPy——科学计算基础库</h4>
    <p class="content_box content_w_710" id="section2-8-1-1">
      NumPy是Numerical
      Python的简称，它是Python科学计算的基础库。NumPy可用来存储和处理大型矩阵，比Python自身的嵌套列表结构更高效，支持大量的多维数组与矩阵运算，也为数组运算提供了大量的数学函数库。
    </p>
    <p class="content_box content_w_710" id="section2-8-1-1">
      此外，由其他语言（如C和Fortran）编写的库也可以直接操作NumPy数组中的数据，无须进行任何数据复制操作。
    </p>
    <h4 class="title_1 content_w_710" id="section2-8-2">1.4.2 SciPy——科学计算扩展库</h4>
    <p class="content_box content_w_710" id="section2-8-2-1">
      SciPy是Python的一个科学计算扩展库，它需要依赖NumPy的支持才能安装和运行。SciPy一般都是操控NumPy的数组来进行科学计算和统计分析，因此可以说SciPy是建立在NumPy基础之上的。SciPy主要在NumPy的基础上增加了数学、科学和工程计算领域中常用的库函数，如线性代数、常微分方程数值求解、信号处理、图像处理、稀疏矩阵等。NumPy和SciPy协同工作，可提高解决问题的效率。
    </p>
    <h4 class="title_1 content_w_710" id="section2-8-3">1.4.3 Pandas——数据分析工具库</h4>
    <p class="content_box content_w_710" id="section2-8-3-1">
      Pandas是一个基于NumPy的免费开源第三方Python库，它可以生成类似于Excel表格式的数据表，而且可以对数据表进行修改操作。Pandas可以从各种格式的文件中提取数据，如CSV文件、JSON文件、SQL数据库、Excel表格等；还可以对各种数据进行合并、转换、选择、清洗和特征加工等运算。
    </p>
    <p class="content_box content_w_710" id="section2-8-3-2">
      Pandas是为解决数据分析任务而创建的工具库，提供了高效操作大型数据集所需的工具，自诞生后就广泛应用于金融、统计学、社会科学、建筑工程等领域。
    </p>
    <h4 class="title_1 content_w_710" id="section2-8-4">1.4.4 Matplotlib——数据可视化扩展库</h4>
    <p class="content_box content_w_710" id="section2-8-4-1">
      Matplotlib是Python中的一个绘图库，支持跨平台运行，可以生成出版级别的图形。Matplotlib能够输出的图形包括折线图、散点图、曲线图、直方图、饼状图、条形图及极坐标图等，其强大的绘图能力能够使用户对数据形成非常清晰直观的认知。
    </p>
    <p class="content_box content_w_710" id="section2-8-4-2">
      <span class="emphasize-tip">【例1-1】</span> 使用Matplotlib绘制
      <img
        class="gs_img gs_img_5"
        src="@/assets/images/bookContent/gs6.png"
        alt=""
        style="width: 10%"
      />
      的图形。
    </p>
    <p class="content_box content_w_710" id="section2-8-4-3" style="margin-bottom: 6px !important">
      <span class="emphasize-tip">【参考代码】</span>
    </p>
    <code class="content_w_710" id="section2-8-4-4">
      <p><span>import numpy as np</span><span>#导入NumPy库</span></p>
      <p>
        <span>import matplotlib.pyplot as plt</span
        ><span>#导入Matplotlib库</span>
      </p>
      <p>
        <span>x=np.arange(-5,5,0.01)</span
        ><span>#设置x的取值范围，设置坐标值</span>
      </p>
      <p><span>y=x*x</span><span>#设置y值，令y=x*x</span></p>
      <p>
        <span>plt.plot(x,y,'k-')</span
        ><span>#绘制曲线，第3个参数表示黑色实线</span>
      </p>
      <p><span>plt.show()</span><span>#显示图形</span></p>
    </code>
    <p class="content_box content_w_710" id="section2-8-4-5">
      <span class="emphasize-tip">【运行结果】</span
      >程序运行结果如图1-12所示。
    </p>
    <div class="demo-image__preview one-img-box" id="section2-8-4-6" style="margin-bottom: 0">
      <el-image
        :src="srcList[11]"
        :preview-src-list="srcList"
        style="width: 70%"
      >
      </el-image>
    </div>
    <span class="little_txt" id="section2-8-4-7">图1-12 使用Matplotlib绘制的图形</span>
    <p class="content_box content_w_710" id="section2-8-4-8">
      <span class="emphasize-tip">【程序说明】</span
      >plot()函数的第3个参数“k-”用来指定线条的颜色和线型，其颜色和线型参数值如表1-10和表1-11所示。
    </p>
    <span class="little_txt" id="section2-8-4-9">表1-10 plot()函数颜色参数值</span>
    <tables
      id="section2-8-4-10"
      class="content_w_710"
      :configTable="table_10.assessConfig"
      :demoTableData="table_10.assessTableData"
    ></tables>
    <span class="little_txt" id="section2-8-4-11">表1-11 plot()函数线型参数值（部分）</span>
    <tables
      id="section2-8-4-12"
      class="content_w_710"
      :configTable="table_11.assessConfig"
      :demoTableData="table_11.assessTableData"
    ></tables>
    <h4 class="title_1 content_w_710" id="section2-8-5">1.4.5 Scikit-learn——机器学习库</h4>
    <p class="content_box content_w_710" id="section2-8-5-1">
      Scikit-learn（简称Sklearn）是Python基于NumPy、SciPy和Matplotlib实现机器学习的算法库，是一个简洁、高效的数据挖掘和数据分析工具。Sklearn基本功能主要分为6大部分：分类、回归、聚类、降维、模型选择和数据预处理。在数据量不大的情况下，Sklearn可以解决大部分问题。对算法不精通的用户在执行建模任务时，并不需要自行编写所有的算法，只需要调用Sklearn库里的模块即可。本教材后续的项目都是基于Sklearn中的算法进行实践的。
    </p>
    <Extend class="content_w_710" />
  </div>
</template>
<script>
import tables from "./tables"; //table组件
import Extend from "./Extend"; //拓展内容组件
export default {
  name: "BookContent",
  components: {
    tables,
    Extend,
  },
  data() {
    return {
      srcList: [
        require("@/assets/images/bookContent/图1-1  机器学习与人类思考的类比.png"),
        require("@/assets/images/bookContent/图1-2  数据相关术语.png"),
        require("@/assets/images/bookContent/图1-3  自然语言处理.png"),
        require("@/assets/images/bookContent/图1-4  自动驾驶汽车.jpeg"),
        require("@/assets/images/bookContent/图1-5  回归任务.png"),
        require("@/assets/images/bookContent/图1-6  分类任务.png"),
        require("@/assets/images/bookContent/图1-7  聚类任务.png"),
        require("@/assets/images/bookContent/图1-8  机器学习的一般过程.png"),
        require("@/assets/images/bookContent/图1-9  手写数字“1”及其对应二维矩阵.png"),
        require("@/assets/images/bookContent/图1-10  过拟合与欠拟合的直观类比.png"),
        require("@/assets/images/bookContent/图1-11  使用模型预测新样本.png"),
        require("@/assets/images/bookContent/图1-12  使用Matplotlib绘制的图形.png"),
      ],
      table_01: {
        assessConfig: [
          { label: "序 号", prop: "number" },
          { label: "长度/（cm）", prop: "length" },
          { label: "质量/（g）", prop: "quality" },
          { label: "材  质", prop: "science" },
          { label: "类  别", prop: "category" },
        ],
        assessTableData: [
          {
            number: "1",
            length: "25",
            quality: "8",
            science: "竹",
            category: "筷子",
          },
          {
            number: "2",
            length: "23",
            quality: "7",
            science: "竹",
            category: "筷子",
          },
          {
            number: "3",
            length: "20",
            quality: "4",
            science: "木",
            category: "筷子",
          },
          {
            number: "4",
            length: "6",
            quality: "0.1",
            science: "竹",
            category: "牙签",
          },
          {
            number: "5",
            length: "5",
            quality: "0.08",
            science: "竹",
            category: "牙签",
          },
          {
            number: "6",
            length: "5.8",
            quality: "0.09",
            science: "竹",
            category: "牙签",
          },
          {
            number: "...",
            length: "...",
            quality: "...",
            science: "...",
            category: "...",
          },
        ],
      },
      table_02: {
        assessConfig: [
          { label: "序  号", prop: "number" },
          { label: "姓  名", prop: "name" },
          { label: "年龄/（岁）", prop: "age" },
          { label: "年收入/（元）", prop: "income" },
          { label: "性  别", prop: "sex" },
          { label: "学  历", prop: "education" },
          { label: "年消费/（元）", prop: "consumption" },
        ],
        assessTableData: [
          {
            number: 1,
            name: "张三",
            age: 36,
            income: 50000,
            sex: "男",
            education: "本科",
            consumption: "30000",
          },
          {
            number: 2,
            name: "赵琦",
            age: 42,
            income: 45000,
            sex: "女",
            education: "本科",
            consumption: "40000",
          },
          {
            number: 3,
            name: "李武",
            age: 23,
            income: 30000,
            sex: "男",
            education: "高中",
            consumption: "",
          },
          {
            number: 4,
            name: "王波",
            age: 61,
            income: 70000,
            sex: "男",
            education: "本科",
            consumption: "20000",
          },
        ],
      },
      table_03: {
        assessConfig: [
          { label: "序  号", prop: "number" },
          { label: "姓  名", prop: "name" },
          { label: "年龄/（岁）", prop: "age" },
          { label: "年收入/（元）", prop: "income" },
          { label: "性  别", prop: "sex" },
          { label: "学  历", prop: "education" },
          { label: "年消费/（元）", prop: "consumption" },
        ],
        assessTableData: [
          {
            number: 5,
            name: "刘玉琦",
            age: 38,
            income: 20000,
            sex: "女",
            education: "大专",
            consumption: "10000",
          },
          {
            number: 6,
            name: "赵琦",
            age: 42,
            income: 45000,
            sex: "女",
            education: "本科",
            consumption: "40000",
          },
          {
            number: 7,
            name: "赵倩",
            age: -5,
            income: 30000,
            sex: "女",
            education: "本科",
            consumption: "90000",
          },
        ],
      },
      table_04: {
        assessConfig: [
          { label: "序  号", prop: "number" },
          { label: "年龄/（岁）", prop: "age" },
          { label: "年收入/（元）", prop: "income" },
          { label: "性  别", prop: "sex" },
          { label: "学  历", prop: "education" },
          { label: "年消费/（元）", prop: "consumption" },
        ],
        assessTableData: [
          {
            number: 1,
            age: 36,
            income: 50000,
            sex: "男",
            education: "本科",
            consumption: "30000",
          },
          {
            number: 2,
            age: 42,
            income: 45000,
            sex: "女",
            education: "本科",
            consumption: "40000",
          },
          {
            number: 3,
            age: 23,
            income: 30000,
            sex: "男",
            education: "高中",
            consumption: "",
          },
          {
            number: 4,
            age: 61,
            income: 70000,
            sex: "男",
            education: "本科",
            consumption: "20000",
          },
          {
            number: 5,
            age: 38,
            income: 20000,
            sex: "女",
            education: "大专",
            consumption: "10000",
          },
          {
            number: 6,
            age: 42,
            income: 45000,
            sex: "女",
            education: "本科",
            consumption: "40000",
          },
          {
            number: 7,
            age: -5,
            income: 30000,
            sex: "女",
            education: "本科",
            consumption: "90000",
          },
        ],
      },
      table_05: {
        assessConfig: [
          { label: "序  号", prop: "number" },
          { label: "年龄/（岁）", prop: "age" },
          { label: "年收入/（元）", prop: "income" },
          { label: "性  别", prop: "sex" },
          { label: "学  历", prop: "education" },
          { label: "年消费/（元）", prop: "consumption" },
        ],
        assessTableData: [
          {
            number: 1,
            age: 36,
            income: 50000,
            sex: "男",
            education: "本科",
            consumption: "30000",
          },
          {
            number: 2,
            age: 42,
            income: 45000,
            sex: "女",
            education: "本科",
            consumption: "40000",
          },
          {
            number: 3,
            age: 23,
            income: 30000,
            sex: "男",
            education: "高中",
            consumption: "15000",
          },
          {
            number: 4,
            age: 61,
            income: 70000,
            sex: "男",
            education: "本科",
            consumption: "20000",
          },
          {
            number: 5,
            age: 38,
            income: 20000,
            sex: "女",
            education: "大专",
            consumption: "10000",
          },
        ],
      },
      table_06: {
        assessConfig: [
          { label: "序  号", prop: "number" },
          { label: "年龄/（岁）", prop: "age" },
          { label: "年收入/（元）", prop: "income" },
          { label: "性  别", prop: "sex" },
          { label: "学  历", prop: "education" },
          { label: "年消费/（元）", prop: "consumption" },
        ],
        assessTableData: [
          {
            number: 1,
            age: 36,
            income: 50000,
            sex: "1",
            education: "60",
            consumption: "30000",
          },
          {
            number: 2,
            age: 42,
            income: 45000,
            sex: "2",
            education: "60",
            consumption: "40000",
          },
          {
            number: 3,
            age: 23,
            income: 30000,
            sex: "1",
            education: "20",
            consumption: "15000",
          },
          {
            number: 4,
            age: 61,
            income: 70000,
            sex: "1",
            education: "60",
            consumption: "20000",
          },
          {
            number: 5,
            age: 38,
            income: 20000,
            sex: "2",
            education: "40",
            consumption: "10000",
          },
        ],
      },
      table_07: {
        assessConfig: [
          { label: "序  号", prop: "number" },
          { label: "年  龄", prop: "age" },
          { label: "年收入", prop: "income" },
          { label: "性  别", prop: "sex" },
          { label: "学  历", prop: "education" },
          { label: "年消费", prop: "consumption" },
        ],
        assessTableData: [
          {
            number: 1,
            age: 0.34,
            income: 0.6,
            sex: "0",
            education: "1",
            consumption: "0.67",
          },
          {
            number: 2,
            age: 0.5,
            income: 0.5,
            sex: "1",
            education: "1",
            consumption: "1",
          },
          {
            number: 3,
            age: 0,
            income: 0.2,
            sex: "0",
            education: "0",
            consumption: "0.17",
          },
          {
            number: 4,
            age: 1,
            income: 1,
            sex: "0",
            education: "1",
            consumption: "0.33",
          },
          {
            number: 5,
            age: 0.39,
            income: 0,
            sex: "1",
            education: "0.5",
            consumption: "0",
          },
        ],
      },
      table_08: {
        assessConfig: [
          { label: "序  号", prop: "number" },
          { label: "年  龄", prop: "age" },
          { label: "年收入", prop: "income" },
          { label: "性  别", prop: "sex" },
          { label: "学  历", prop: "education" },
          { label: "年消费", prop: "consumption" },
        ],
        assessTableData: [
          {
            number: 1,
            age: -0.33,
            income: 0.41,
            sex: "-0.82",
            education: "0.75",
            consumption: "0.65",
          },
          {
            number: 2,
            age: 0.16,
            income: 0.12,
            sex: "1.22",
            education: "0.75",
            consumption: "1.58",
          },
          {
            number: 3,
            age: -1.38,
            income: -0.76,
            sex: "-0.82",
            education: "-1.75",
            consumption: "-0.74",
          },
          {
            number: 4,
            age: 1.71,
            income: 1.56,
            sex: "-0.82",
            education: "0.75",
            consumption: "-0.28",
          },
          {
            number: 5,
            age: -0.16,
            income: -1.34,
            sex: "1.22",
            education: "-0.5",
            consumption: "-1.21",
          },
        ],
      },
      table_09: {
        assessConfig: [
          { label: "方 法 名", prop: "methods" },
          { label: "是否需要真实值监控", prop: "monitor" },
          { label: "最 佳 值", prop: "bestVal" },
        ],
        assessTableData: [
          {
            methods: "ARI（兰德系数）评价法",
            monitor: "需要",
            bestVal: "1.0",
          },
          {
            methods: "AMI（互信息）评价法",
            monitor: "需要",
            bestVal: "1.0",
          },
          {
            methods: "V-measure评分",
            monitor: "需要",
            bestVal: "1.0",
          },
          {
            methods: "FMI评价法",
            monitor: "需要",
            bestVal: "1.0",
          },
          {
            methods: "轮廓系数评价法",
            monitor: "不需要",
            bestVal: "畸变程度最大",
          },
          {
            methods: "calinski_harabasz指数评价法",
            monitor: "不需要",
            bestVal: "相比较最大",
          },
        ],
      },
      table_10: {
        assessConfig: [
          { label: "蓝", prop: "blue" },
          { label: "绿", prop: "green" },
          { label: "红", prop: "red" },
          { label: "青", prop: "young" },
          { label: "品  红", prop: "Magenta" },
          { label: "黄", prop: "yellow" },
          { label: "黑", prop: "black" },
          { label: "白", prop: "white" },
        ],
        assessTableData: [
          {
            blue: "'b'",
            green: "'g'",
            red: "'r'",
            young: "'c'",
            Magenta: "'m'",
            yellow: "'y'",
            black: "'k'",
            white: "'w'",
          },
        ],
      },
      table_11: {
        assessConfig: [
          { label: "实  线", prop: "line1" },
          { label: "虚  线", prop: "line2" },
          { label: "点  线", prop: "line3" },
          { label: "星  型", prop: "line4" },
          { label: "正 方 形", prop: "line5" },
          { label: "五 边 形", prop: "line6" },
          { label: "加  号", prop: "line7" },
          { label: "正 三 角", prop: "line8" },
        ],
        assessTableData: [
          {
            line1: "'-'",
            line2: "'--'",
            line3: "':'",
            line4: "'*'",
            line5: "'s'",
            line6: "'p'",
            line7: "'+'",
            line8: "'^'",
          },
        ],
      },
      tableData: [
        {
          date: "正例",
          just: "真正例（TP）",
          back: "假反例（FN）",
        },
        {
          date: "反例",
          just: "假正例（FP）",
          back: "真反例（TN）",
        },
      ],
    }
  },
  methods: {
    playVideo(url) {
      this.$emit('playVideo', url)
    },
  }
}
</script>
<style lang="scss" scoped>
@import "@/assets/styles/bookContent.scss";
p{
  img{
    max-width: 100%;
    display: block;
  }

  .bubble-font {
    color: #0281EC;
    position: relative;
    cursor: pointer;
    word-break: keep-all;
  }
  
  .bubble-icon {
    width: 14px;
    height: 14px;
    display: inline;
    top: -10px;
    position: relative;
  }

  .icon{
    text-indent: 0;
  }
}
h3{
  font-size: 32px;
  color: #98e1e2;
  line-height: 32px;
  margin-bottom: 10px;
}
.center{
  text-indent: 0 !important;
}
.chapter_title{
  width: 100%;
  height: 206px;
  background: url(~@/assets/images/bookContent/chapter_title_bg.png) center top no-repeat;
  background-size: contain;

  >span{
    display: block;
    font-size: 32px;
    font-weight: bold;
    color: #98e1e2;
    width: 60%;
    margin: 0 auto;
    text-align: center;
    letter-spacing: 6px;

    &:first-child {
      padding-top: 38px;
    }
  }
}

.target_title{
  margin: 20px 0;
  width: 100%;
  height: 70px;
  background: url(~@/assets/images/bookContent/title_bg_xm.png) center center no-repeat;
  background-size: contain;
  font-size: 28px;
  color: #fff;
  text-align: center;
  line-height: 70px;
}
.content_box {
  text-indent: 40px;
  user-select: text;
}
.title_1{
  font-size: 26px;
  color: #00a8ac;
}
.title_2{
  color: #00a8ac;
  margin-bottom: 10px;
  font-size: 20px;
  font-weight: normal;
}
.one-img-box{
  width: 100%;
  min-height: 150px;
  height: auto;
  max-height: 320px;
  overflow: hidden;
  text-align: center;
  margin-bottom: 10px;
}
.little_txt {
  display: block;
  margin-bottom: 10px;
  width: 100%;
  text-align: center;
  font-size: 16px;
  color: #00a8ac;
}

.one-video-box {
  position: relative;
  margin: 20px auto;

  .play-btn {
    position: absolute;
    bottom: 20px;
    right: 20px;
    align-items: center;
    justify-content: center;
    border-radius: 30px;
    width: 238px;
    height: 58px;
    color: $theme_color;
    border: 1px solid $theme_color;
    background: rgba(7, 170, 174, 0.1);
    cursor: pointer;

    i {
      font-size: 26px;
      margin-right: 10px;
    }
  }

  .play-btn:hover {
    background: rgba(7, 170, 174, 0.2);
  }
  
  .v-title {
    position: absolute;
    top: 80px;
    right: 20px;
    width: 400px;

    p {
      font-size: 28px;
      font-weight: bold;
    }
  }
}
code {
  display: block;
  padding: 12px 20px;
  margin-bottom: $gap_20;
  width: 100%;
  border-radius: $border_radius;
  box-shadow: $box_shadow;
  background: #eee;
  border: $border_1;

  p {
    display: flex;
    font-size: 18px;
    text-indent: 0;

    span {
      width: 48%;
      line-height: 1.3;
      margin-bottom: 8px;
      word-break: break-all;
      user-select: none !important;
    }

    span:first-child {
      margin-right: 4%;
    }
  }
}
.content_w_710 {
  width: calc(100% - 40px) !important;
  margin: 0 auto 20px;
}
</style>